import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useCreation, useMemoizedFn, useRequest } from 'ahooks';
import { Form, FormItemProps, FormInstance, Tooltip } from 'antd';
import CheckboxTree, { Node } from 'components/CheckboxTree';
import { StoreScopesModal } from 'components/StoreScopes';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import useModal from 'hooks/useModal';
import { compact, groupBy, sortBy, uniq, uniqBy, without } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  permissionDependencies,
  permissionDependents,
  PermissionV2,
} from 'services/api/department/permissions';
import { enableWa } from 'services/api/whatsapp';
import { useAllPermissionsV2 } from '../../hooks/data';
import {
  usePermissionDescriptionLabel,
  usePermissionGroupNameLabel,
  usePermissionNameLabel,
} from '../../hooks/usePermissionLabel';
import ScopePreviewModal from '../ScopePreviewModal';
import ScopeTag from '../ScopeTag';
import { useQAPairOpened } from 'hooks/business/im/useQAPair';

const Question = styled(QuestionCircleOutlined)`
  margin-right: 6px;
  font-weight: 400;
  color: var(--theme-text-3);
  cursor: help;
`;

const waPermissions: General.PermissionName[] = ['WA消息模版'];

interface RoleWithDepartment extends StoreHomeV2.RoleWithDepartment {}
interface Department extends StoreHomeV2.Department {}

const FormItem = Form.Item;
type PermissionWithScopes = RoleWithDepartment['permissionWithScopes'];

type Value = {
  permissionId: PermissionV2['permissionId'];
  platformStoreScopes: General.StoreScope[];
};
export interface PermissionsFieldsProps extends FormItemProps {
  departmentId: Department['id'];
  disabled?: boolean;
  name?: string;
  departmentScopes?: General.StoreScope[];
  initialValue: Array<Value>;
  assignBuyerAndOrderRule?: {
    isAssignAfterSalesOrder: boolean;
    isAssignNegativeReview: boolean;
  };
}
const PermissionsFields = (props: PermissionsFieldsProps) => {
  const {
    name = 'permissionWithScopes',
    departmentScopes,
    departmentId,
    disabled,
    /**
     * 由于 form.getFiledValue 需要在mounted后才获取，直接透传会比较快
     * 传入的initialValue会认为是manualChecked，用于处理前置依赖是否需要取消
     */
    initialValue,
    assignBuyerAndOrderRule,
    ...otherProps
  } = props;
  const { t } = useTranslate();
  const permissionNameLabel = usePermissionNameLabel();
  const permissionGroupNameLabel = usePermissionGroupNameLabel();
  const permissionDescriptionLabel = usePermissionDescriptionLabel();
  const { data: permissionsRaw } = useAllPermissionsV2();
  const storeScopeModal = useModal<PermissionV2['permissionId']>();
  const { data: isWaEnable } = useRequest(enableWa);
  const {
    whiteListConfig: {
      SCORE_SCENARIO = false,
      USE_ITEM_GROUP = false,
      ERP = false,
      KNOWLEDGE_SINGLE_ITEM = false,
    } = {},
  } = useOrgConfigStateValue();
  const qaPairOpened = useQAPairOpened();
  const { permissionGroups, groupIdxOfPermissions } = useCreation(() => {
    const groupIdxOfPermissions: Record<string, number> = {};
    if (!permissionsRaw) {
      return { permissionGroups: [], groupIdxOfPermissions };
    }
    const groups = groupBy(permissionsRaw, 'permissionGroupName');
    const permissionGroups = Object.keys(groups).reduce((res, groupKey, groupIdx) => {
      if (
        (groupKey === '第三方权限' && !ERP) ||
        (groupKey === 'AI运营' && !qaPairOpened && !KNOWLEDGE_SINGLE_ITEM)
      ) {
        res.push(null);
        return res;
      }
      const groupChildren = groups[groupKey]
        .filter(({ permissionName, __LOCAL__: { disabled = false } = {} }) => {
          if (disabled || permissionName === '管理员视角') {
            return false;
          }

          if (!SCORE_SCENARIO && permissionName === '配置接待评分任务') {
            return false;
          }

          if (!USE_ITEM_GROUP && permissionName === '商品组') {
            return false;
          }

          if (!KNOWLEDGE_SINGLE_ITEM && permissionName === '商品知识') {
            return false;
          }

          if (
            !qaPairOpened &&
            (permissionName === '提交AI无法回复问题' || permissionName === '提交AI回复错误问题')
          ) {
            return false;
          }

          return isWaEnable || !waPermissions.includes(permissionName);
        })
        .map((p) => {
          groupIdxOfPermissions[`${p.permissionId}`] = groupIdx;
          const isAssignCauseDisabled =
            ((p.permissionId === 5 || p.permissionId === 6) &&
              assignBuyerAndOrderRule?.isAssignAfterSalesOrder) ||
            (p.permissionId === 35 && assignBuyerAndOrderRule?.isAssignNegativeReview);
          return {
            key: `${p.permissionId}`,
            value: p.permissionId,
            label: permissionNameLabel[p.permissionName] || p.permissionName,
            disabled: p?.__LOCAL__?.disabled || isAssignCauseDisabled,
            raw: isAssignCauseDisabled ? undefined : p,
          };
        });
      const node: Node<PermissionV2> = {
        key: groupKey,
        value: groupKey,
        label:
          permissionGroupNameLabel[groupKey as keyof typeof permissionGroupNameLabel] || groupKey,
        virtual: true,
        children:
          groupKey === '配置中心'
            ? sortBy(groupChildren, (each) => {
                return {
                  '37': 0,
                  '45': 1,
                  '36': 2,
                  '38': 3,
                  '46': 4,
                }[each.key];
              })
            : groupChildren,
      };
      res.push(node);
      return res;
    }, [] as (Node<PermissionV2> | null)[]);
    return { permissionGroups, groupIdxOfPermissions };
  }, [permissionsRaw, isWaEnable, assignBuyerAndOrderRule]);
  const permissionMapRef = useRef<
    Record<PermissionV2['permissionId'], PermissionWithScopes[number]>
  >({});
  const permissionNameMapRef = useRef<Record<PermissionV2['permissionName'], PermissionV2>>(
    {} as any,
  );
  const manualSelectPermissionsNamesRef = useRef<PermissionV2['permissionName'][]>([]);
  useEffect(() => {
    if (permissionsRaw) {
      permissionNameMapRef.current = {} as any;
      permissionsRaw.forEach((p) => {
        permissionNameMapRef.current[p.permissionName] = p;
      });
    }
  }, [permissionsRaw]);

  useEffect(() => {
    if (!permissionsRaw?.length || !initialValue?.length) {
      return;
    }
    initialValue.forEach((value) => {
      const point = permissionsRaw?.find((t) => t.permissionId === value.permissionId);
      if (!point?.permissionName) {
        return;
      }
      manualSelectPermissionsNamesRef.current = uniq([
        ...manualSelectPermissionsNamesRef.current,
        point.permissionName,
      ]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsRaw]);

  const updatePermissionWithScopes = useMemoizedFn(
    (nextPermission: PermissionWithScopes[number], form?: FormInstance) => {
      if (!form) {
        return;
      }
      const { getFieldValue, setFieldsValue } = form;
      const olgPermissionWithScopes: RoleWithDepartment['permissionWithScopes'] =
        getFieldValue(name) || [];
      const permissionWithScopes = olgPermissionWithScopes.filter(
        (n) => n.permissionId !== nextPermission.permissionId,
      );
      permissionWithScopes.push(nextPermission);
      setFieldsValue({ [name]: permissionWithScopes });
    },
  );
  /**
   * permissionName: 为空时不处理
   */
  const dealWithManualChecked = useMemoizedFn(
    (permissionName?: PermissionV2['permissionName'], mode: 'remove' | 'add' = 'add') => {
      if (!permissionName) {
        return;
      }
      if (mode === 'remove') {
        manualSelectPermissionsNamesRef.current = without<PermissionV2['permissionName']>(
          manualSelectPermissionsNamesRef.current,
          permissionName,
        );
        return;
      }
      manualSelectPermissionsNamesRef.current.push(permissionName);
      manualSelectPermissionsNamesRef.current = uniq(manualSelectPermissionsNamesRef.current);
    },
  );
  const removeFromManualMap = useMemoizedFn((node: Node<PermissionV2>) => {
    const permissionName = node?.raw?.permissionName;
    const hasChildren = Boolean(node?.children?.length);

    dealWithManualChecked(permissionName, 'remove');
    if (!hasChildren) {
      return;
    }
    node.children?.forEach(removeFromManualMap);
  });
  const addToManualMap = useMemoizedFn((node: Node<PermissionV2>) => {
    const permissionName = node?.raw?.permissionName;
    const hasChildren = Boolean(node?.children?.length);
    dealWithManualChecked(permissionName);
    if (!hasChildren) {
      return;
    }
    node.children?.forEach(addToManualMap);
  });
  const patchPermissionDeps = useMemoizedFn((selectedPermissions: PermissionV2[]) => {
    // 为选中的权限点列表补充依赖的前置权限
    const withDeps = selectedPermissions.reduce(
      (res, permission) => {
        const { permissionName } = permission;
        if (!permissionName) {
          return res;
        }
        const deps = permissionDependencies[permissionName];
        if (permissionsRaw && deps && deps?.length > 0) {
          const depsPoint = permissionsRaw
            // @ts-expect-error
            .filter((t) => deps.includes(t.permissionName));
          // eslint-disable-next-line no-param-reassign
          return res.concat(depsPoint);
        }
        return res;
      },
      [...selectedPermissions],
    );
    return uniqBy(withDeps, 'permissionId');
  });
  const dealWithPermissionUnChecked = useMemoizedFn(
    (selectedNodesRaw: Node<PermissionV2>[], triggerNode: Node<PermissionV2>) => {
      // 此处 处理某个节点(triggerNode)被取消选择的情况
      // 使用辅助变量 manualSelectPermissionsNamesRef 记录所有手动选中的节点
      // 每次有节点被取消时，
      // 可以认为存在selectedNodesRaw，而不在manualSelectPermissionsNamesRef列表内的节点都是因应主动选中的节点而被动添加进去的依赖项，
      // 因此不在manualSelectPermissionsNamesRef中的节点可以直接过滤，后期再将依赖项补充完整。
      const triggerPermission = triggerNode?.raw;
      if (triggerPermission && triggerPermission.permissionName) {
        removeFromManualMap(triggerNode);
        // 当取消的节点本身是作为前置依赖而被动添加为selectedNode的时候
        // 例如nodeA是真正的手动添加节点，存在于manualSelectPermissionsNamesRef
        // 但是此时nodeB作为依赖项也存在于selectedNodesRaw中，而不存在于manualSelectPermissionsNamesRef
        // 此时nodeB 被取消，作为nodeA的前置依赖，nodeA也需要被取消
        // 因此需要把使用nodeB作为前置依赖的所有子项的手动添加记录消除（即从manualSelectPermissionsNamesRef移除）
        const dependents = permissionDependents[triggerPermission.permissionName];
        dependents?.forEach?.((d) => dealWithManualChecked(d, 'remove'));
      }
      const manualSelectedNodes = selectedNodesRaw.reduce((selectedNodes, node) => {
        const nodePermissionName = node?.raw?.permissionName;
        // permissionName是后续处理的关键属性，若当前selectedNode没有permissionName，可以直接忽略后续补正处理
        if (!nodePermissionName) {
          return selectedNodes;
        }
        // 只添加手动checked的权限节点
        if (manualSelectPermissionsNamesRef.current.includes(nodePermissionName)) {
          selectedNodes.push(node);
        }
        return selectedNodes;
      }, [] as Node<PermissionV2>[]);
      return manualSelectedNodes;
    },
  );
  return (
    <FormItem name={name} {...otherProps}>
      <FormItem noStyle dependencies={[name]}>
        {(form) => {
          const { getFieldValue, setFieldsValue } = form;
          const permissionsWithScope: PermissionWithScopes = getFieldValue(name) || [];
          permissionMapRef.current = {};
          const permissionMap = permissionMapRef.current;
          const selectedKeyGroup = permissionsWithScope.reduce((res, permission) => {
            const idx = groupIdxOfPermissions[permission.permissionId];
            res[idx] = res[idx] || [];
            res[idx].push(`${permission.permissionId}`);
            permissionMap[permission.permissionId] = permission;
            return res;
          }, [] as Array<string[]>);

          const rolePermScopeType = getFieldValue('rolePermScopeType');

          const isAllScope = rolePermScopeType === 'ALL_PERM_ALIGNED';

          const trees = permissionGroups.map((permissionGroup, scopeIdx) => {
            if (!permissionGroup) {
              return null;
            }
            const selectedKeys = selectedKeyGroup[scopeIdx];
            return (
              <CheckboxTree<PermissionV2>
                disabled={disabled}
                key={permissionGroup.key}
                className="mb-3"
                labelRender={(node, checked, nodeDisabled) => {
                  if (checked && nodeDisabled) {
                    return <span>{node.label}</span>;
                  }
                  return node.label;
                }}
                labelExtraRender={(node, checked) => {
                  const { raw: permission } = node;

                  if (!permission || !checked || isAllScope) {
                    return null;
                  }

                  const disableScopes = permission?.__LOCAL__?.disabledScopes;

                  if (disableScopes) {
                    return null;
                  }

                  const { permissionId, permissionName, permissionGroupName } = permission;
                  const scopes = permissionMap[permissionId]?.platformStoreScopes || [];

                  const tag = (
                    <ScopeTag
                      value={scopes}
                      departmentScopes={departmentScopes}
                      disabled={disabled}
                      onClick={() => storeScopeModal.show(permissionId)}
                      onClear={() => {
                        updatePermissionWithScopes(
                          {
                            permissionId,
                            permissionName,
                            permissionGroupName,
                            platformStoreScopes: [],
                          },
                          // @ts-expect-error
                          form,
                        );
                      }}
                    />
                  );

                  if (disabled) {
                    return tag;
                  }

                  return <Tooltip title={t('department.修改范围')}>{tag}</Tooltip>;
                }}
                labelSuffixRender={(node) => {
                  const { raw: permission } = node;

                  if (!permission) {
                    return null;
                  }

                  const { permissionName } = permission;

                  const description = permissionDescriptionLabel[permissionName];

                  if (!description) {
                    return null;
                  }

                  return (
                    <Tooltip title={description}>
                      <Question />
                    </Tooltip>
                  );
                }}
                dataSource={permissionGroup}
                selectedKeys={selectedKeys}
                onChange={(_nextSelectedKeys, selectedNodes, { node: triggerNode, checked }) => {
                  if (checked) {
                    addToManualMap(triggerNode);
                  } else {
                    removeFromManualMap(triggerNode);
                  }
                  const validNodes =
                    checked || !triggerNode.raw
                      ? selectedNodes
                      : dealWithPermissionUnChecked(selectedNodes, triggerNode);
                  const selectedPermissionsRaw = patchPermissionDeps(
                    compact(validNodes.map((n) => n.raw)),
                  );
                  let nextPermissions = selectedPermissionsRaw.reduce(
                    (res, permission) => {
                      const { permissionId, permissionName, permissionGroupName } = permission;
                      res.push({
                        permissionId,
                        permissionName,
                        permissionGroupName,
                        platformStoreScopes: permissionMap[permissionId]?.platformStoreScopes || [],
                      });
                      return res;
                    },
                    permissionsWithScope.filter((target) => {
                      if (!selectedKeys) {
                        return true;
                      }
                      return !selectedKeys.includes(`${target.permissionId}`);
                    }),
                  );
                  nextPermissions = uniqBy(nextPermissions, 'permissionId');
                  setFieldsValue({
                    permissionWithScopes: nextPermissions,
                  });
                }}
              />
            );
          });

          const value =
            storeScopeModal.meta === undefined
              ? []
              : permissionMapRef.current[storeScopeModal.meta]?.platformStoreScopes || [];

          return (
            <>
              {trees}
              {disabled ? (
                <ScopePreviewModal
                  value={value}
                  visible={storeScopeModal.visible}
                  onCancel={() => storeScopeModal.hide()}
                />
              ) : (
                <StoreScopesModal
                  showFooterTips={false}
                  departmentId={departmentId}
                  visible={storeScopeModal.visible}
                  destroyOnClose
                  value={value}
                  onCancel={() => storeScopeModal.hide()}
                  onChange={(scopes) => {
                    if (storeScopeModal.meta === undefined) {
                      return;
                    }

                    const { permissionName, permissionGroupName } =
                      permissionMap[storeScopeModal.meta] || {};

                    updatePermissionWithScopes(
                      {
                        permissionId: storeScopeModal.meta,
                        permissionName,
                        permissionGroupName,
                        platformStoreScopes: scopes,
                      },
                      // @ts-expect-error
                      form,
                    );
                  }}
                />
              )}
            </>
          );
        }}
      </FormItem>
    </FormItem>
  );
};
export default PermissionsFields;
